import {
  Box, Grid,
  Stack,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import { observer } from "mobx-react";
import { FC, useCallback, useEffect, useMemo, useState } from "react";

import { useStore } from "../../hooks";
import { IGoodsItem } from "../../store/goods-store";
import { Delete2Icon, EditIcon } from "../icons";
import Loader from "../loader/loader";
import Table, { Cell } from "../table/table";
import StyledIconButton from "../ui/StyledIconButton";
import AddFieldForm from "./components/add-field-form";
import Filters from "./components/filters";
import locale from "./locale";
import styles from "./styles";

interface IGoodsProps {
  showAddForm: boolean;
  setShowAddForm: (showAddForm: boolean) => void;
}

const Goods: FC<IGoodsProps> = ({ showAddForm, setShowAddForm }) => {
  const {
    goodsStore: {
      fetchGoods,
      addGood,
      editGood,
      deleteGood,
      setPaginationParams,
      goods,
      paginationParams,
      totalCount,
      isLoading,
    },
    popupStore
  } = useStore();
  const [editData, setEditData] = useState<IGoodsItem | undefined>();

  const headerCells = useMemo(() => locale.headerCells, []);

  const onAdd = useCallback(
    (data: IGoodsItem) => {
      if (Boolean(editData)) {
        editGood(data);
      } else {
        addGood(data);
      }

      setEditData(undefined);
      setShowAddForm(false);
    },
    [editData, addGood, editGood, setShowAddForm]
  );

  const onCancelAdd = useCallback(() => {
    setEditData(undefined);
    setShowAddForm(false);
  }, [setShowAddForm]);

  const onRemoveField = useCallback((data: IGoodsItem) => {
    if (data.id) {
      deleteGood(data.id);
    }
  }, [deleteGood]);

  const onEditField = useCallback((id: number) => {
    const editToken = goods?.find((token) => token.id === id);
    setEditData(editToken);
    setShowAddForm(true);
  }, [goods, setShowAddForm]);

  const renderHeaderCell = useCallback(
    (cell: Cell) => (
      <TableCell variant="footer" key={cell.name} align="left" width={cell.width}>
        {cell.name === "edit" || cell.name === "delete" ? "" : cell.name}
      </TableCell>
    ),
    []
  );

  const renderRow = useCallback(
    (field: IGoodsItem, index: number) => {
      return (
        <TableRow key={index}>
          <TableCell align="left">
            {field.wbClientLabel}
          </TableCell>
          <TableCell align="left">
            {field.ozonClientLabel}
          </TableCell>
          <TableCell align="left">{field.brand}</TableCell>
          <TableCell align="left">{field.subject}</TableCell>
          <TableCell align="left">{field.sellerArticle}</TableCell>
          <TableCell align="left">{field.wbArticle}</TableCell>
          <TableCell align="left">{field.barcode}</TableCell>
          <TableCell align="left">{field.ozonSku}</TableCell>
          <TableCell align="left">{field.size}</TableCell>
          <TableCell align="left">{field.deliveryTime}</TableCell>
          <TableCell align="left">{field.costPrice}</TableCell>
          <TableCell align="left">{field.priceApplicationDate}</TableCell>
          <TableCell align="left">{field.manager}</TableCell>
          <TableCell align="left">{field.description}</TableCell>
          <TableCell align="left">{field.active ? "Да" : "Нет"}</TableCell>

          {field.id && (
            <TableCell align="left">
              <Stack direction="row" gap={1}>
                <StyledIconButton size="small" onClick={() => onEditField(field.id!)}>
                  <EditIcon />
                </StyledIconButton>
                <StyledIconButton size="small" onClick={() => onRemoveField(field)}>
                  <Delete2Icon />
                </StyledIconButton>
              </Stack>
            </TableCell>
          )}
        </TableRow>
      );
    },
    [onEditField, onRemoveField]
  );

  const onChangePage = useCallback((page: number) => {
    setPaginationParams({ page });
  }, [setPaginationParams]);

  const onChangePerPage = useCallback((value: number) => {
    setPaginationParams({ limit: value, page: 1 });
  }, [setPaginationParams]);

  useEffect(() => {
    fetchGoods();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationParams]);

  useEffect(() => {
    if (showAddForm) {
      popupStore.openPopup({
        props: {
          size: "md",
          onClose: onCancelAdd,
          title: !!editData ? "Редактировать товар" : "Добавить новый товар"
        },
        contentProps: {
          onAdd,
          onCancel: onCancelAdd,
          defaultData: editData
        },
        content: AddFieldForm,
      });
    } else {
      popupStore.closePopup();
    }
  }, [onCancelAdd, editData, onAdd, showAddForm, popupStore]);


  return (
    <Box sx={styles.wrapper}>
      <Grid container spacing={3} mb="18px">
        <Grid item sm={2}>
        </Grid>
        <Grid item sm={10}>
          <Filters />
        </Grid>
      </Grid>

      {!goods?.length && !isLoading && (
        <Typography variant="caption" mt={4}>
          Нет товаров подходящих под фильтры, либо вы пока не добавили товары
        </Typography>
      )}
      {Boolean(goods?.length) && (
        <Table
          headerCells={headerCells}
          fields={goods as IGoodsItem[]}
          renderHeaderCell={renderHeaderCell}
          renderRow={renderRow}
          totalCount={totalCount}
          onChangePage={onChangePage}
          onChangePerPage={onChangePerPage}
          sx={styles.table}
        />
      )}

      <Loader isLoading={isLoading} />
    </Box>
  );
};

export default observer(Goods);
